import {
  PathsV1BranchSearchPostParametersQueryExtend,
  PathsV1RelocationOrderSearchPostParametersQueryExtend,
  RelocationOrderStatus,
} from "@/api-schema-enums";
import { useBranchSearchQuery } from "@/common/api/branch/use-branch-search.query";
import { useRelocationOrderSearchQuery } from "@/common/api/relocation-order/use-relocation-order-search.query";
import { useMapTheme } from "@/components/deck-map/_helpers/map-theme";
import { CenteredLayout, CenteredLayoutVariant } from "@/components/layouts/centered-layout";
import { CommandButton } from "@/components/command-palette/command-button";
import { ButtonColorVariant, ButtonSize, ButtonVariant } from "@/components/ui/button";
import { Loader, LoaderSize } from "@/components/ui/loader";
import { Monitor, MonitorOff } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MapLegend } from "./_components/map-legend";
import { TransitMap } from "./_components/transit-map";
import {
  ACTIVE_STATUSES_LIST,
  ARRIVING_STATUSES,
  IN_TRANSIT_STATUSES,
  ISSUE_STATUSES,
  PENDING_STATUSES,
} from "./map-statuses";

export function RelocationOrderMapView() {
  const { t } = useTranslation(["fit", "common"]);
  const palette = useMapTheme();
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const [isKioskMode, setIsKioskMode] = useState(false);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsKioskMode(document.fullscreenElement === mapContainerRef.current);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  const toggleKioskMode = async () => {
    if (!mapContainerRef.current) return;

    if (!document.fullscreenElement) {
      try {
        await mapContainerRef.current.requestFullscreen();
      } catch (err) {
        console.error("Error attempting to enable fullscreen:", err);
      }
    } else {
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      }
    }
  };

  const { data: branchData, isLoading: branchesLoading } = useBranchSearchQuery({
    options: {
      parameters: {
        pageSize: 500,
        extend: [PathsV1BranchSearchPostParametersQueryExtend.Address],
      },
    },
    queryOptions: {
      refetchInterval: 60000,
    },
  });

  const { data: ordersData, isLoading: ordersLoading } = useRelocationOrderSearchQuery({
    options: {
      parameters: {
        pageSize: 500,
        extend: [
          PathsV1RelocationOrderSearchPostParametersQueryExtend.OriginBranch,
          PathsV1RelocationOrderSearchPostParametersQueryExtend.OriginBranch_Address,
          PathsV1RelocationOrderSearchPostParametersQueryExtend.OriginAddress,
          PathsV1RelocationOrderSearchPostParametersQueryExtend.CurrentBranch,
          PathsV1RelocationOrderSearchPostParametersQueryExtend.CurrentBranch_Address,
          PathsV1RelocationOrderSearchPostParametersQueryExtend.CurrentAddress,
          PathsV1RelocationOrderSearchPostParametersQueryExtend.DestinationBranch,
          PathsV1RelocationOrderSearchPostParametersQueryExtend.DestinationBranch_Address,
          PathsV1RelocationOrderSearchPostParametersQueryExtend.DestinationAddress,
          PathsV1RelocationOrderSearchPostParametersQueryExtend.Vehicle,
          PathsV1RelocationOrderSearchPostParametersQueryExtend.Vehicle_ModelGeneration,
          PathsV1RelocationOrderSearchPostParametersQueryExtend.Vehicle_ModelGeneration_ModelFamily,
          PathsV1RelocationOrderSearchPostParametersQueryExtend.Vehicle_ModelGeneration_ModelFamily_Brand,
        ],
      },
      body: { status: ACTIVE_STATUSES_LIST },
    },
    queryOptions: {
      refetchInterval: 60000,
    },
  });

  const branches = useMemo(() => branchData?.data ?? [], [branchData?.data]);
  const branchCount = useMemo(() => branches.filter((b) => b.address?.latitude != null).length, [branches]);

  const orders = useMemo(() => ordersData?.data ?? [], [ordersData?.data]);

  const stats = useMemo(() => {
    let active = 0;
    let pending = 0;
    let arriving = 0;
    let issue = 0;

    for (const order of orders) {
      const status = order.status as RelocationOrderStatus;
      if (IN_TRANSIT_STATUSES.has(status)) active++;
      else if (PENDING_STATUSES.has(status)) pending++;
      else if (ARRIVING_STATUSES.has(status)) arriving++;
      else if (ISSUE_STATUSES.has(status)) issue++;
    }

    return { active, pending, arriving, issue };
  }, [orders]);

  const isLoading = branchesLoading || ordersLoading;

  return (
    <CenteredLayout
      variant={CenteredLayoutVariant.Table}
      title={t("fit:TransitMap")}
      subTitle={t("fit:TransitMapDescription")}
    >
      <div ref={mapContainerRef} className="relative min-h-0 flex-1">
        {isLoading ? (
          <div className="flex h-full items-center justify-center">
            <Loader size={LoaderSize.Medium} />
          </div>
        ) : (
          <>
            <TransitMap branches={branches} relocationOrders={orders} palette={palette} isKioskMode={isKioskMode} />
            <div className="absolute bottom-4 left-4 z-10 flex flex-col gap-2">
              <CommandButton
                commandId="fit-relocation-order-map-kiosk-mode"
                label={isKioskMode ? t("fit:ExitKioskMode", "Exit Kiosk") : t("fit:EnterKioskMode", "Kiosk Mode")}
                icon={isKioskMode ? <MonitorOff className="h-4 w-4" /> : <Monitor className="h-4 w-4" />}
                variant={ButtonVariant.Filled}
                colorVariant={ButtonColorVariant.Secondary}
                size={ButtonSize.Sm}
                onClick={toggleKioskMode}
              />
              <MapLegend
                activeCount={stats.active}
                pendingCount={stats.pending}
                arrivingCount={stats.arriving}
                issueCount={stats.issue}
                branchCount={branchCount}
                totalOrders={orders.length}
                palette={palette}
              />
            </div>
          </>
        )}
      </div>
    </CenteredLayout>
  );
}
